import { Square, CheckSquare, Trash2, Edit2, XCircle, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useModal } from "../../../hooks/useModal";

interface TodoTag {
  id: string;
  name: string;
  color: string;
}

interface TodoItem {
  id: string;
  text: string;
  completed: boolean;
  cancelled: boolean;
  priority: "low" | "medium" | "high" | "critical";
  todoTags: TodoTag[];
  estimatedTimeValue?: number;
  estimatedTimeUnit?: "minutes" | "hours" | "days";
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  todoCategoryId: string;
  isTask?: boolean;
}

interface TodoItemProps {
  item: TodoItem;
  onToggleComplete: (categoryId: string, itemId: string) => void;
  onCancelItem: (categoryId: string, itemId: string) => void;
  onEditItem: (categoryId: string, itemId: string) => void;
  onDeleteItem: (categoryId: string, itemId: string) => void;
  canUpdate?: boolean;
  canDelete?: boolean;
  canToggle?: boolean;
}

export default function TodoItemComponent({
  item,
  onToggleComplete,
  onCancelItem,
  onEditItem,
  onDeleteItem,
  canUpdate = true,
  canDelete = true,
  canToggle = true,
}: TodoItemProps) {
  const navigate = useNavigate();
  const { showAlert } = useModal();

  const getPriorityBadge = (priority: "low" | "medium" | "high" | "critical") => {
    const styles = {
      low: "bg-blue-100 text-blue-800",
      medium: "bg-green-100 text-green-800",
      high: "bg-orange-100 text-orange-800",
      critical: "bg-red-100 text-red-800",
    };

    const labels = {
      low: "Düşük",
      medium: "Orta",
      high: "Yüksek",
      critical: "Kritik",
    };

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[priority]}`}>
        {labels[priority]}
      </span>
    );
  };

  const formatEstimatedTime = (value?: number, unit?: "minutes" | "hours" | "days") => {
    if (!value || value === 0 || !unit) return null;
    const unitLabels = {
      minutes: "dakika",
      hours: "saat",
      days: "gün",
    };
    return `${value} ${unitLabels[unit]}`;
  };

  const calculateDuration = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffMs = endDate.getTime() - startDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const minutes = diffMins % 60;
    return { hours, minutes };
  };

  const handleToggleComplete = async () => {
    if (item.isTask) {
      await showAlert({
        title: "İşlem Yapılamıyor",
        message: "Görevler modülü TODO'larının durumu değiştirilemez. Lütfen Görevler modülünü kullanın.",
        type: "warning",
      });
      return;
    }
    onToggleComplete(item.todoCategoryId, item.id);
  };

  return (
    <div className="px-3 sm:px-4 py-3 hover:bg-gray-50 transition-colors">
      <div className="flex items-start gap-2 sm:gap-3">
        <button
          onClick={handleToggleComplete}
          className={`flex-shrink-0 mt-0.5 ${item.isTask || !canToggle ? "cursor-not-allowed opacity-60" : ""}`}
          disabled={item.isTask || !canToggle}>
          {item.completed ? (
            <CheckSquare className="h-5 w-5 text-green-600" />
          ) : item.cancelled ? (
            <XCircle className="h-5 w-5 text-red-600" />
          ) : (
            <Square className={`h-5 w-5 ${item.isTask || !canToggle ? "text-gray-300" : "text-gray-400 hover:text-gray-600"}`} />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <p
            className={`text-sm mb-2 break-words ${
              item.completed ? "line-through text-gray-400" : item.cancelled ? "line-through text-red-400" : "text-gray-900"
            }`}>
            {item.text}
          </p>
          <div className="flex flex-wrap gap-1.5 sm:gap-2 items-center">
            {getPriorityBadge(item.priority)}

            {item.estimatedTimeValue && item.estimatedTimeUnit && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                <Clock className="w-3 h-3 mr-1" />
                {formatEstimatedTime(item.estimatedTimeValue, item.estimatedTimeUnit)}
              </span>
            )}

            {item.todoTags?.map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white"
                style={{ backgroundColor: tag.color }}>
                {tag.name}
              </span>
            ))}

            {item.completed && item.startedAt && item.completedAt && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                ✓ Süre:{" "}
                {(() => {
                  const { hours, minutes } = calculateDuration(item.startedAt, item.completedAt);
                  return hours > 0 ? `${hours}s ${minutes}dk` : `${minutes}dk`;
                })()}
              </span>
            )}
          </div>
        </div>
        {!item.isTask && (canUpdate || canToggle || canDelete) && (
          <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
            {canUpdate && (
              <button
                onClick={() => onEditItem(item.todoCategoryId, item.id)}
                className="p-1 sm:p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                title="Düzenle">
                <Edit2 className="h-4 w-4" />
              </button>
            )}
            {canToggle && (
              <button
                onClick={() => onCancelItem(item.todoCategoryId, item.id)}
                className="p-1 sm:p-1.5 text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-colors"
                title={item.cancelled ? "İptali Geri Al" : "İptal Et"}>
                <XCircle className="h-4 w-4" />
              </button>
            )}
            {canDelete && (
              <button
                onClick={() => onDeleteItem(item.todoCategoryId, item.id)}
                className="p-1 sm:p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                title="Sil">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
        {item.isTask && !item.completed && (
          <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
            <button
              onClick={() => navigate("/tasks?tab=assigned-tasks")}
              className="px-2 py-1 text-xs text-purple-700 bg-purple-100 hover:bg-purple-200 rounded-md font-medium transition-colors cursor-pointer">
              Görevler Modülü
            </button>
          </div>
        )}
        {item.isTask && item.completed && canDelete && (
          <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
            <button
              onClick={() => onDeleteItem(item.todoCategoryId, item.id)}
              className="p-1 sm:p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
              title="Sil">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
